/**
 * Fil d'entraide étudiant — questions, ressources, annonces.
 *
 * Design sobre, mobile-first : liste défilante avec chips filtres, création
 * inline, détail post avec réponses + votes utiles.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Filter, Plus, HelpCircle, BookOpen, Megaphone,
  ThumbsUp, MessageSquare, CheckCircle2, Loader2, X, Trash2, Flag,
  RefreshCw, ChevronRight, Award, Send,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import {
  portailApi, EntraidePostItem, EntraideReponseItem,
} from '../services/portailApi';

type Cat = 'question' | 'ressource' | 'annonce';

const CATS: { key: Cat; label: string; Icon: React.ComponentType<any>; color: string }[] = [
  { key: 'question', label: 'Question', Icon: HelpCircle, color: 'text-blue-400' },
  { key: 'ressource', label: 'Ressource', Icon: BookOpen, color: 'text-emerald-400' },
  { key: 'annonce', label: 'Annonce', Icon: Megaphone, color: 'text-amber-400' },
];

function fmtRelatif(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24);
  if (j === 1) return 'hier';
  if (j < 7) return `il y a ${j} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function initiales(nom: string): string {
  return (nom || '?').split(' ').slice(0, 2).map(s => s[0] || '').join('').toUpperCase();
}

export default function Entraide() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const openPostId = sp.get('post') ? Number(sp.get('post')) : null;

  const [posts, setPosts] = useState<EntraidePostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [categorie, setCategorie] = useState<Cat | ''>('');
  const [niveau, setNiveau] = useState<string>('');
  const [ordre, setOrdre] = useState<'recent' | 'populaire'>('recent');
  const [showCreate, setShowCreate] = useState(false);

  const charger = () => {
    setLoading(true);
    setError(null);
    portailApi.entraideListe({
      q: q || undefined,
      categorie: categorie || undefined,
      niveau: niveau || undefined,
      ordre,
      limit: 50,
    })
      .then((r) => setPosts(r.results))
      .catch((e: any) => setError(e?.message || 'Erreur chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [categorie, niveau, ordre]);
  // Recherche debounce sur q
  useEffect(() => {
    const t = setTimeout(charger, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const handleVote = async (p: EntraidePostItem) => {
    try {
      const r = await portailApi.entraideVote(p.id);
      setPosts(prev => prev.map(x => x.id === p.id
        ? { ...x, a_vote: r.a_vote, nb_votes: r.nb_votes }
        : x));
    } catch (e: any) { alert(e?.message || 'Erreur vote'); }
  };

  return (
    <PortailLayout showHeader title="Entraide">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/portail/menu')}
            className="p-2 hover:bg-gray-800 rounded text-gray-300"
            title="Retour">
            <ArrowLeft size={18} />
          </button>
          <h2 className="font-semibold text-gray-100 text-sm flex-1">Fil d'entraide</h2>
          <button onClick={charger}
            className="p-2 hover:bg-gray-800 rounded text-gray-400"
            title="Rafraîchir">
            <RefreshCw size={14} />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg">
            <Plus size={14} /> Publier
          </button>
        </div>

        {/* Barre recherche + filtres */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-2 space-y-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-gray-500" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Rechercher un sujet, une matière…"
              className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500" />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <button onClick={() => setCategorie('')}
              className={`px-2 py-0.5 text-[11px] rounded border ${
                categorie === '' ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700'
              }`}>Tout</button>
            {CATS.map(({ key, label, Icon, color }) => (
              <button key={key} onClick={() => setCategorie(categorie === key ? '' : key)}
                className={`px-2 py-0.5 text-[11px] rounded border inline-flex items-center gap-1 ${
                  categorie === key ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700'
                }`}>
                <Icon size={10} className={categorie === key ? 'text-white' : color} />
                {label}
              </button>
            ))}
            <span className="text-gray-600 mx-0.5">·</span>
            <select value={niveau} onChange={e => setNiveau(e.target.value)}
              className="px-1.5 py-0.5 text-[11px] rounded border bg-gray-800 border-gray-700 text-gray-300">
              <option value="">Tous niveaux</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>
            <select value={ordre} onChange={e => setOrdre(e.target.value as any)}
              className="px-1.5 py-0.5 text-[11px] rounded border bg-gray-800 border-gray-700 text-gray-300">
              <option value="recent">Récent</option>
              <option value="populaire">Populaire</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 text-xs text-red-300">
            {error}
          </div>
        )}

        {loading && !posts.length ? (
          <div className="text-center py-10 text-gray-500 text-sm">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <MessageSquare className="inline w-10 h-10 text-gray-500 mb-2" />
            <p className="text-gray-300 text-sm">Aucun post dans le fil pour ces filtres.</p>
            <p className="text-xs text-gray-500 mt-2">Sois le premier à poser une question ou partager une ressource.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {posts.map(p => (
              <PostCard key={p.id} p={p} onOpen={() => setSp({ post: String(p.id) })} onVote={() => handleVote(p)} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <ModalCreatePost
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); setPosts(x => [p, ...x]); setSp({ post: String(p.id) }); }}
        />
      )}

      {openPostId && (
        <ModalPostDetail
          postId={openPostId}
          onClose={() => setSp({})}
          onDeleted={() => { setSp({}); charger(); }}
          onChanged={() => charger()}
        />
      )}
    </PortailLayout>
  );
}

// ─── Carte de post ───────────────────────────────────────────────────────
const PostCard: React.FC<{
  p: EntraidePostItem;
  onOpen: () => void;
  onVote: () => void;
}> = ({ p, onOpen, onVote }) => {
  const cat = CATS.find(c => c.key === p.categorie);
  const CatIcon = cat?.Icon || HelpCircle;
  return (
    <div className={`bg-gray-900 border ${p.resolu ? 'border-emerald-800' : 'border-gray-800'} rounded-lg p-3`}>
      <div className="flex items-start gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-800`}>
          <CatIcon size={14} className={cat?.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {p.resolu && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300 font-semibold inline-flex items-center gap-0.5">
                <CheckCircle2 size={10} /> Résolu
              </span>
            )}
            <button onClick={onOpen} className="text-sm font-semibold text-gray-100 text-left hover:text-blue-400 truncate">
              {p.titre}
            </button>
          </div>
          <p className="text-[12px] text-gray-400 mt-0.5 line-clamp-2">{p.contenu}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500 flex-wrap">
            <span>{p.auteur_nom}</span>
            {p.auteur_classe && <span className="text-gray-600">· {p.auteur_classe}</span>}
            {p.matiere_liee && <span className="text-blue-400">· {p.matiere_liee}</span>}
            <span className="text-gray-600">· {fmtRelatif(p.date_creation)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-800/60">
        <button onClick={onVote}
          className={`inline-flex items-center gap-1 text-xs ${
            p.a_vote ? 'text-blue-400 font-semibold' : 'text-gray-400 hover:text-blue-400'
          }`}
          disabled={p.suis_auteur}
          title={p.suis_auteur ? 'Impossible de voter pour votre propre post' : 'Utile'}>
          <ThumbsUp size={12} className={p.a_vote ? 'fill-blue-400' : ''} /> {p.nb_votes}
        </button>
        <button onClick={onOpen} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200">
          <MessageSquare size={12} /> {p.nb_reponses}
        </button>
        <div className="flex-1" />
        <button onClick={onOpen} className="text-[10px] text-blue-400 hover:underline inline-flex items-center gap-0.5">
          Ouvrir <ChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

// ─── Modal : nouveau post ────────────────────────────────────────────────
const ModalCreatePost: React.FC<{
  onClose: () => void;
  onCreated: (p: EntraidePostItem) => void;
}> = ({ onClose, onCreated }) => {
  const [titre, setTitre] = useState('');
  const [contenu, setContenu] = useState('');
  const [categorie, setCategorie] = useState<Cat>('question');
  const [matiere, setMatiere] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!titre.trim()) { alert('Titre requis'); return; }
    if (!contenu.trim()) { alert('Contenu requis'); return; }
    setSaving(true);
    try {
      const p = await portailApi.entraideCreer({
        titre: titre.trim(), contenu: contenu.trim(),
        categorie, matiere_liee: matiere.trim(),
      });
      onCreated(p);
    } catch (e: any) {
      alert(e?.message || 'Erreur publication');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-end sm:items-center justify-center p-3"
      onClick={() => !saving && onClose()}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl bg-gray-900 border-2 border-gray-800 shadow-2xl">
        <header className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-gray-100">Nouvelle publication</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200"><X size={18} /></button>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Catégorie</label>
            <div className="flex gap-1">
              {CATS.map(({ key, label, Icon, color }) => (
                <button key={key} onClick={() => setCategorie(key)}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg border font-medium ${
                    categorie === key ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700'
                  }`}>
                  <Icon size={12} className={categorie === key ? '' : color} />
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Titre *</label>
            <input value={titre} onChange={e => setTitre(e.target.value)} maxLength={300} autoFocus
              placeholder={categorie === 'question' ? 'Ex : Comment résoudre cet exercice de math ?' : categorie === 'ressource' ? 'Ex : Résumé de cours L1 Anglais' : 'Ex : Cherche partenaire pour projet groupe'}
              className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Contenu *</label>
            <textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={5}
              placeholder={categorie === 'question' ? 'Décris ta question avec le maximum de contexte…' : 'Décris ce que tu partages…'}
              className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-sm resize-y" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Matière (optionnel)</label>
            <input value={matiere} onChange={e => setMatiere(e.target.value)} maxLength={200}
              placeholder="Ex : Mathématiques II, Anglais…"
              className="w-full px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-sm" />
          </div>
        </div>
        <footer className="px-4 py-3 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-md bg-gray-800 text-gray-200 hover:bg-gray-700">
            Annuler
          </button>
          <button onClick={submit} disabled={saving || !titre.trim() || !contenu.trim()}
            className="inline-flex items-center gap-1 px-4 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? 'Publication…' : 'Publier'}
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── Modal : détail d'un post + réponses ────────────────────────────────
const ModalPostDetail: React.FC<{
  postId: number;
  onClose: () => void;
  onDeleted: () => void;
  onChanged: () => void;
}> = ({ postId, onClose, onDeleted, onChanged }) => {
  const [post, setPost] = useState<EntraidePostItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const charger = () => {
    setLoading(true);
    portailApi.entraideDetail(postId)
      .then(setPost)
      .catch((e: any) => alert(e?.message || 'Erreur'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [postId]);

  const cat = post ? CATS.find(c => c.key === post.categorie) : null;
  const CatIcon = cat?.Icon || HelpCircle;

  const handleVotePost = async () => {
    if (!post) return;
    try {
      const r = await portailApi.entraideVote(post.id);
      setPost({ ...post, a_vote: r.a_vote, nb_votes: r.nb_votes });
      onChanged();
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleVoteReponse = async (r: EntraideReponseItem) => {
    try {
      const res = await portailApi.entraideReponseVote(r.id);
      setPost(p => p ? {
        ...p,
        reponses: (p.reponses || []).map(x => x.id === r.id
          ? { ...x, a_vote: res.a_vote, nb_votes: res.nb_votes } : x),
      } : p);
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleAccepter = async (r: EntraideReponseItem) => {
    try {
      const res = await portailApi.entraideReponseAccepter(r.id);
      // Reload full pour reset acceptees des autres + statut resolu
      charger();
      onChanged();
      return res;
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleSubmitReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await portailApi.entraideRepondre(postId, reply.trim());
      setReply('');
      setPost(p => p ? {
        ...p,
        reponses: [...(p.reponses || []), r],
        nb_reponses: p.nb_reponses + 1,
      } : p);
      onChanged();
    } catch (e: any) { alert(e?.message || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  const handleToggleResolu = async () => {
    if (!post) return;
    try {
      const r = await portailApi.entraideToggleResolu(post.id);
      setPost({ ...post, resolu: r.resolu });
      onChanged();
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleSupprimerPost = async () => {
    if (!post) return;
    if (!window.confirm('Supprimer cette publication ? Cette action est définitive.')) return;
    try {
      await portailApi.entraideSupprimer(post.id);
      onDeleted();
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleSupprimerReponse = async (r: EntraideReponseItem) => {
    if (!window.confirm('Supprimer cette réponse ?')) return;
    try {
      await portailApi.entraideReponseSupprimer(r.id);
      setPost(p => p ? {
        ...p,
        reponses: (p.reponses || []).filter(x => x.id !== r.id),
        nb_reponses: Math.max(0, p.nb_reponses - 1),
      } : p);
      onChanged();
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  const handleSignaler = async () => {
    if (!post) return;
    const raison = window.prompt('Raison (spam / offensant / inapproprie / erreur / autre) :', 'inapproprie');
    if (!raison) return;
    try {
      await portailApi.entraideSignaler(post.id, raison.trim());
      alert('Merci — le signalement a été transmis.');
    } catch (e: any) { alert(e?.message || 'Erreur'); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-end sm:items-center justify-center p-3">
      <div className="w-full max-w-2xl max-h-[95vh] flex flex-col rounded-xl bg-gray-900 border-2 border-gray-800 shadow-2xl">
        <header className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-gray-100 truncate">
            {loading ? 'Chargement…' : (post?.titre || 'Publication')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading || !post ? (
            <div className="text-center py-8 text-gray-500">
              <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : (
            <>
              {/* Post principal */}
              <div className="bg-gray-800/50 border border-gray-800 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <CatIcon size={16} className={cat?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        post.categorie === 'question' ? 'bg-blue-900/40 text-blue-300' :
                        post.categorie === 'ressource' ? 'bg-emerald-900/40 text-emerald-300' :
                        'bg-amber-900/40 text-amber-300'
                      } font-semibold uppercase`}>{cat?.label}</span>
                      {post.resolu && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300 font-semibold inline-flex items-center gap-0.5">
                          <CheckCircle2 size={10} /> Résolu
                        </span>
                      )}
                      {post.matiere_liee && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{post.matiere_liee}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {post.auteur_nom} {post.auteur_classe && `· ${post.auteur_classe}`} · {fmtRelatif(post.date_creation)}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-200 whitespace-pre-wrap">{post.contenu}</p>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-800/60">
                  <button onClick={handleVotePost} disabled={post.suis_auteur}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                      post.a_vote ? 'bg-blue-900/40 text-blue-300' : 'text-gray-400 hover:text-blue-400'
                    } disabled:opacity-50`}>
                    <ThumbsUp size={12} className={post.a_vote ? 'fill-blue-400' : ''} /> {post.nb_votes} utile{post.nb_votes > 1 ? 's' : ''}
                  </button>
                  {post.suis_auteur && (
                    <button onClick={handleToggleResolu}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                        post.resolu ? 'bg-emerald-900/40 text-emerald-300' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}>
                      <CheckCircle2 size={12} /> {post.resolu ? 'Marquer non résolu' : 'Marquer résolu'}
                    </button>
                  )}
                  <div className="flex-1" />
                  {post.suis_auteur ? (
                    <button onClick={handleSupprimerPost} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                      <Trash2 size={11} /> Supprimer
                    </button>
                  ) : (
                    <button onClick={handleSignaler} className="text-xs text-gray-500 hover:text-red-400 inline-flex items-center gap-1"
                      title="Signaler ce contenu">
                      <Flag size={11} /> Signaler
                    </button>
                  )}
                </div>
              </div>

              {/* Réponses */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2 inline-flex items-center gap-1">
                  <MessageSquare size={11} /> Réponses ({post.reponses?.length || 0})
                </div>
                {(post.reponses?.length || 0) === 0 ? (
                  <p className="text-xs text-gray-500 italic">Aucune réponse pour le moment. Sois le premier à aider.</p>
                ) : (
                  <div className="space-y-2">
                    {post.reponses!.map(r => (
                      <div key={r.id}
                        className={`rounded-lg border p-2.5 ${
                          r.acceptee
                            ? 'bg-emerald-950/20 border-emerald-800'
                            : 'bg-gray-800/40 border-gray-800'
                        }`}>
                        <div className="flex items-start gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-300 flex-shrink-0">
                            {initiales(r.auteur_nom)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold text-gray-200">{r.auteur_nom}</span>
                              {r.auteur_classe && <span className="text-[10px] text-gray-500">· {r.auteur_classe}</span>}
                              {r.acceptee && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-800 text-white inline-flex items-center gap-0.5">
                                  <Award size={9} /> Meilleure réponse
                                </span>
                              )}
                              <span className="text-[10px] text-gray-500 ml-auto">{fmtRelatif(r.date_creation)}</span>
                            </div>
                            <p className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{r.contenu}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <button onClick={() => handleVoteReponse(r)} disabled={r.suis_auteur}
                                className={`inline-flex items-center gap-1 text-[11px] ${
                                  r.a_vote ? 'text-blue-400' : 'text-gray-500 hover:text-blue-400'
                                } disabled:opacity-50`}>
                                <ThumbsUp size={11} className={r.a_vote ? 'fill-blue-400' : ''} /> {r.nb_votes}
                              </button>
                              {post.suis_auteur && (
                                <button onClick={() => handleAccepter(r)}
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    r.acceptee ? 'bg-emerald-800 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}>
                                  {r.acceptee ? 'Décocher meilleure' : 'Marquer meilleure'}
                                </button>
                              )}
                              <div className="flex-1" />
                              {r.suis_auteur && (
                                <button onClick={() => handleSupprimerReponse(r)}
                                  className="text-[11px] text-red-400 hover:text-red-300">
                                  <Trash2 size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {/* Composer réponse */}
        <div className="border-t border-gray-800 p-3 flex items-end gap-2">
          <textarea value={reply} onChange={e => setReply(e.target.value)}
            placeholder="Aider avec une réponse…"
            rows={1}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-sm resize-none overflow-y-auto"
            style={{ maxHeight: '120px', minHeight: '38px' }} />
          <button onClick={handleSubmitReply} disabled={sending || !reply.trim()}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};
